import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { PORTRAIT_URL } from "@/data/cases";
import { useLanguage } from "@/i18n/LanguageContext";

export const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const { t } = useLanguage();

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-28"
    >
      <div className="pointer-events-none absolute -right-40 top-1/4 h-[34rem] w-[34rem] rounded-full bg-[#FF3333]/10 blur-[120px]" />

      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
        <MaskedLine delay={0.1}>
          <span className="mb-8 inline-block text-sm uppercase tracking-[0.3em] text-[#A3A3A3]">
            {t("hero.label")}
          </span>
        </MaskedLine>
        <h1 className="font-syne font-extrabold uppercase leading-[0.9] tracking-tighter text-[#F2F2F2]">
          <MaskedLine delay={0.25} innerClassName="text-[clamp(2.75rem,8vw,6rem)]">
            {t("hero.l1")}
          </MaskedLine>
          <MaskedLine delay={0.38} innerClassName="text-[clamp(2.75rem,8vw,6rem)]">
            {t("hero.l2")}
          </MaskedLine>
          <MaskedLine delay={0.51} innerClassName="text-[clamp(2.75rem,8vw,6rem)] text-[#FF3333]">
            {t("hero.l3")}
          </MaskedLine>
        </h1>

        <div className="mt-14 grid grid-cols-1 items-end gap-12 lg:grid-cols-[minmax(0,1fr)_auto]">
          <div>
            <MaskedLine delay={0.7}>
              <p className="max-w-md text-base leading-relaxed text-[#A3A3A3] md:text-lg">
                {t("hero.sub")}
              </p>
            </MaskedLine>
            <MaskedLine delay={0.85}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <button
                  data-testid="hero-cta-contact"
                  onClick={() => onNavigate("kontakt")}
                  className="group flex items-center gap-2 rounded-full bg-[#FF3333] px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105"
                >
                  {t("hero.cta1")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </button>
                <button
                  data-testid="hero-cta-cases"
                  onClick={() => onNavigate("cases")}
                  className="group flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-[#F2F2F2] transition-colors duration-300 hover:border-[#FF3333] hover:text-[#FF3333]"
                >
                  {t("hero.cta2")}
                  <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
                </button>
              </div>
            </MaskedLine>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative hidden lg:block"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -inset-6 rounded-t-full bg-[#FF3333]/15 blur-2xl" />
              <div className="relative h-[21rem] w-64 overflow-hidden rounded-t-full border border-white/10">
                <img
                  src={PORTRAIT_URL}
                  alt="Webudvikler bag Ny hjemmeside NU"
                  data-testid="hero-portrait"
                  className="h-full w-full scale-[1.12] object-cover grayscale transition-all duration-700 hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-5 -left-10 rounded-2xl border border-white/10 bg-black/60 px-6 py-4 backdrop-blur-xl">
                <p className="font-syne text-2xl font-bold text-[#FF3333]">&lt; 24t</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">{t("hero.chip")}</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <ArrowDown className="h-5 w-5 animate-bounce text-[#A3A3A3]" />
      </motion.div>
    </section>
  );
};
